import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import coachService from '../services/endpoints/coachService';
import userService from '../services/endpoints/userService';
import { CoachInvite, CoachClient, MyCoach } from '../types';
import { parseLocalDate } from '../utils/dateUtils';

type Tab = 'profile' | 'share' | 'clients';

const Settings: React.FC = () => {
  const { user, isLoading: userLoading } = useCurrentUser();
  const [searchParams] = useSearchParams();

  // Allow deep-linking to a specific tab via ?tab=clients, ?tab=share, etc.
  const tabParam = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && ['profile', 'share', 'clients'].includes(tabParam) ? tabParam : 'profile'
  );

  if (userLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading settings...</div>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'profile', label: 'Profile', icon: '👤' },
    { key: 'share', label: 'Share with Coach', icon: '🔗' },
    { key: 'clients', label: 'My Athletes', icon: '🏋️' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gold-500">Settings</h1>

      {/* Tab bar */}
      <div className="flex space-x-1 bg-dark-800 rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-dark-600 text-gold-500 shadow-sm'
                : 'text-gray-400 hover:text-white hover:bg-dark-700'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'profile' && <ProfileTab user={user} />}
      {activeTab === 'share' && <ShareWithCoachTab />}
      {activeTab === 'clients' && <CoachedClientsTab user={user} />}
    </div>
  );
};

// ─── Profile Tab ────────────────────────────────────────────────────────────

const ProfileTab: React.FC<{ user: any }> = ({ user }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setDisplayName(user.displayName || '');
    }
  }, [user]);

  if (!user) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      await userService.updateProfile({ email: email.trim(), displayName: displayName.trim() });
      setStatus({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setStatus({ type: 'error', text: 'Failed to save profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const readOnlyFields = [
    { label: 'First Name', value: user.firstName },
    { label: 'Last Name', value: user.lastName },
    { label: 'Role', value: user.isCoach ? 'Bodybuilder & Coach' : 'Bodybuilder' },
    { label: 'Member Since', value: parseLocalDate(user.createdAt).toLocaleDateString() },
    { label: 'Last Login', value: user.lastLoginAt ? parseLocalDate(user.lastLoginAt).toLocaleString() : 'N/A' },
  ];

  return (
    <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
      <h2 className="text-xl font-semibold text-white mb-4">Your Profile</h2>

      {status && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center ${
          status.type === 'success'
            ? 'bg-green-600/20 border border-green-600/30 text-green-400'
            : 'bg-red-600/20 border border-red-600/30 text-red-400'
        }`}>
          <span className="mr-2">{status.type === 'success' ? '✅' : '❌'}</span>
          {status.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Editable fields */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full text-white bg-dark-700 border border-dark-600 rounded px-3 py-2 focus:outline-none focus:border-gold-500"
            placeholder="your@email.com"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full text-white bg-dark-700 border border-dark-600 rounded px-3 py-2 focus:outline-none focus:border-gold-500"
            placeholder="Display name"
          />
        </div>

        {/* Read-only fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-dark-700">
          {readOnlyFields.map((field) => (
            <div key={field.label}>
              <label className="block text-sm text-gray-400 mb-1">{field.label}</label>
              <div className="text-gray-300 bg-dark-700/50 rounded px-3 py-2 text-sm">{field.value || '—'}</div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-gold-500 hover:bg-gold-400 text-black font-semibold rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Share with Coach Tab ───────────────────────────────────────────────────

const ShareWithCoachTab: React.FC = () => {
  const [invites, setInvites] = useState<CoachInvite[]>([]);
  const [coaches, setCoaches] = useState<MyCoach[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [inv, coa] = await Promise.all([
        coachService.getMyInvites(),
        coachService.getMyCoaches(),
      ]);
      setInvites(inv);
      setCoaches(coa);
    } catch (err) {
      console.error('Failed to load coach sharing data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await coachService.generateInvite();
      await loadData();
    } catch (err) {
      console.error('Failed to generate invite', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    if (!window.confirm('Delete this invite code?')) return;
    try {
      await coachService.deleteInvite(inviteId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete invite', err);
    }
  };

  const handleRevokeCoach = async (relationshipId: string) => {
    if (!window.confirm('Revoke this coach\'s access to your data?')) return;
    try {
      await coachService.revokeRelationship(relationshipId);
      await loadData();
    } catch (err) {
      console.error('Failed to revoke coach', err);
    }
  };

  const copyToClipboard = (code: string, inviteId: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(inviteId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return <div className="text-gray-400 p-6">Loading...</div>;
  }

  const activeInvites = invites.filter(i => !i.isUsed && new Date(i.expiresAt) > new Date());
  const usedOrExpiredInvites = invites.filter(i => i.isUsed || new Date(i.expiresAt) <= new Date());

  return (
    <div className="space-y-6">
      {/* Generate Invite */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Generate Invite Code</h2>
            <p className="text-sm text-gray-400 mt-1">
              Create a code to share with your coach. Codes expire in 7 days and can only be used once.
            </p>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 bg-gold-600 hover:bg-gold-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {generating ? 'Generating...' : '+ Generate Code'}
          </button>
        </div>

        {/* Active invite codes */}
        {activeInvites.length > 0 && (
          <div className="space-y-3">
            {activeInvites.map((invite) => (
              <div key={invite.inviteId} className="bg-dark-700 rounded-lg p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-mono text-lg text-gold-400 tracking-wider select-all">
                    {invite.inviteCode}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Expires {parseLocalDate(invite.expiresAt).toLocaleDateString()} • Created {parseLocalDate(invite.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => copyToClipboard(invite.inviteCode, invite.inviteId)}
                    className="px-3 py-1.5 bg-dark-600 hover:bg-dark-500 text-white rounded text-sm transition-colors"
                  >
                    {copiedId === invite.inviteId ? '✓ Copied' : '📋 Copy'}
                  </button>
                  <button
                    onClick={() => handleDeleteInvite(invite.inviteId)}
                    className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-sm transition-colors"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeInvites.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            No active invite codes. Generate one to share with your coach.
          </div>
        )}
      </div>

      {/* My Coaches */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4">My Coaches</h2>
        {coaches.length > 0 ? (
          <div className="space-y-3">
            {coaches.map((coach) => (
              <div key={coach.relationshipId} className="bg-dark-700 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">{coach.coachName}</div>
                  <div className="text-sm text-gray-400">{coach.coachEmail}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Connected since {parseLocalDate(coach.connectedSince).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeCoach(coach.relationshipId)}
                  className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-sm transition-colors"
                >
                  Revoke Access
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            No coaches connected yet. Generate an invite code and share it with your coach.
          </div>
        )}
      </div>

      {/* History */}
      {usedOrExpiredInvites.length > 0 && (
        <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-semibold text-gray-400 mb-3">Invite History</h2>
          <div className="space-y-2">
            {usedOrExpiredInvites.map((invite) => (
              <div key={invite.inviteId} className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between text-sm">
                <div className="font-mono text-gray-500">{invite.inviteCode}</div>
                <div className="text-gray-500">
                  {invite.isUsed
                    ? `✓ Redeemed by ${invite.redeemedByCoachName || 'Unknown'}`
                    : '⏰ Expired'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Coached Clients Tab ────────────────────────────────────────────────────

const CoachedClientsTab: React.FC<{ user: any }> = ({ user }) => {
  const [clients, setClients] = useState<CoachClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState<{ success: boolean; message: string } | null>(null);

  const loadClients = useCallback(async () => {
    try {
      const data = await coachService.getClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    setRedeemResult(null);
    try {
      const result = await coachService.redeemInvite(redeemCode.trim());
      setRedeemResult({ success: true, message: `${result.message} You are now coaching ${result.athleteName}.` });
      setRedeemCode('');
      await loadClients();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to redeem invite code.';
      setRedeemResult({ success: false, message: msg });
    } finally {
      setRedeeming(false);
    }
  };

  const handleRevokeClient = async (relationshipId: string) => {
    if (!window.confirm('Remove this athlete? They will need to generate a new invite code to reconnect.')) return;
    try {
      await coachService.revokeRelationship(relationshipId);
      await loadClients();
    } catch (err) {
      console.error('Failed to revoke client', err);
    }
  };

  if (loading) {
    return <div className="text-gray-400 p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Redeem Invite Code */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-2">Add an Athlete</h2>
        <p className="text-sm text-gray-400 mb-4">
          Paste the invite code from your bodybuilder to connect and view their data.
        </p>
        <div className="flex space-x-3">
          <input
            type="text"
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value)}
            placeholder="Paste invite code here..."
            className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-4 py-2.5 text-white font-mono placeholder-gray-500 focus:outline-none focus:border-gold-500"
            onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
          />
          <button
            onClick={handleRedeem}
            disabled={redeeming || !redeemCode.trim()}
            className="px-6 py-2.5 bg-gold-600 hover:bg-gold-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {redeeming ? 'Connecting...' : 'Connect'}
          </button>
        </div>
        {redeemResult && (
          <div className={`mt-3 px-4 py-2 rounded-lg text-sm ${
            redeemResult.success
              ? 'bg-green-600/20 text-green-400 border border-green-600/30'
              : 'bg-red-600/20 text-red-400 border border-red-600/30'
          }`}>
            {redeemResult.message}
          </div>
        )}
      </div>

      {/* Client List */}
      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4">
          Your Athletes {clients.length > 0 && <span className="text-gray-400 text-base font-normal">({clients.length})</span>}
        </h2>
        {clients.length > 0 ? (
          <div className="space-y-3">
            {clients.map((client) => (
              <div key={client.relationshipId} className="bg-dark-700 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">{client.athleteName}</div>
                  <div className="text-sm text-gray-400">{client.athleteEmail}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Connected since {parseLocalDate(client.connectedSince).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeClient(client.relationshipId)}
                  className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-sm transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-3">🏋️</div>
            <p>No athletes connected yet.</p>
            <p className="text-sm mt-1">Ask your bodybuilders to generate an invite code from their Settings page.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
